/**
 * Leads, customers and recurring contracts.
 *
 * Design requirement from the brief: there must be a reason to choose one
 * customer over another. Leads therefore differ on three axes at once - payout,
 * how long the job will take, and how far away it is - so the interesting
 * question is never "which pays most" but "which pays most per hour of my day,
 * including the walk".
 *
 * Contracts are the on-ramp to the financial-freedom fantasy: recurring, lower
 * per-visit pay in exchange for predictability, and the thing an employee can
 * be pointed at while the player does something else.
 */
import type { GameState, Lead, Customer, CustomerTraits } from '../state/GameState.js';
import { Rng } from '../core/rng.js';
import type { EventBus } from '../core/events.js';
import type { IdGenerator } from '../core/ids.js';
import type { ReputationSystem } from './ReputationSystem.js';
import { BALANCE } from '../data/balance.js';
import { CUSTOMER_LOTS, distanceBetween } from '../data/world.js';
import { VEHICLES_BY_ID } from '../data/catalog.js';
import { FIRST_NAMES, LAST_NAMES } from '../data/catalog.js';

/** Households sharing each customer lot. See `seedCustomers`. */
export const HOUSEHOLDS_PER_LOT = 3;

export class CustomerSystem {
  constructor(
    private readonly bus: EventBus,
    private readonly rng: Rng,
    private readonly ids: IdGenerator,
  ) {}

  /**
   * Seed the neighbourhood's households.
   *
   * Each customer lot represents a short stretch of street rather than a single
   * house, so it holds several households. That is not cosmetic: a lead is only
   * generated for a customer who has no open lead, so with one household per lot
   * the entire neighbourhood could never offer more than twelve jobs at once. A
   * balance sweep showed the consequence plainly - a player with one $60 mower
   * matched a fully equipped one, and employees had nothing to do, because both
   * simply exhausted the neighbourhood every day. Demand has to be able to
   * outgrow a single pair of hands for the equipment ladder or hiring to mean
   * anything.
   */
  seedCustomers(state: GameState): void {
    // Traits come from their OWN stream, seeded from the game seed, not from
    // the shared one. Drawing four extra floats per customer out of the
    // shared RNG shifts every downstream draw in the whole simulation - and
    // it did: the balance sweep's median goal day went from 16 to 54 and two
    // dominant-strategy guards failed, from a change whose every multiplier
    // was mean-centred. This project has been bitten by exactly this once
    // before, when weather was drawn from the shared stream.
    const traitRng = new Rng(state.seed ^ 0x7a1e5);
    const customers = [];
    for (const lot of CUSTOMER_LOTS) {
      for (let unit = 0; unit < HOUSEHOLDS_PER_LOT; unit++) {
        customers.push({
          id: this.ids.next('cust'),
          name: `${this.rng.pick(FIRST_NAMES)} ${this.rng.pick(LAST_NAMES)}`,
          locationId: lot.id,
          traits: {
            // Drawn once, from the shared seeded stream, so the same seed
            // always produces the same neighbourhood of people.
            expectation: traitRng.float(...TRAIT_RANGE.expectation),
            patience: traitRng.float(...TRAIT_RANGE.patience),
            generosity: traitRng.float(...TRAIT_RANGE.generosity),
            difficulty: traitRng.float(...TRAIT_RANGE.difficulty),
          },
          satisfaction: 0.5,
          jobsCompleted: 0,
          contract: null,
          lastServicedDay: null,
        });
      }
    }
    state.customers = customers;
  }

  /**
   * Lead volume scales with reputation. This is the main loop's feedback: do
   * good work, get more work offered, choose the better ones.
   */
  leadsForToday(state: GameState): number {
    const cfg = BALANCE.leads;
    const raw =
      cfg.baseLeadsPerDay + state.player.reputation * cfg.leadsPerReputationPoint;
    const capped = Math.min(cfg.maxLeadsPerDay, raw);
    const whole = Math.floor(capped);
    return whole + (this.rng.next() < capped - whole ? 1 : 0);
  }

  generateDailyLeads(state: GameState, demandMultiplier = 1): void {
    const count = Math.round(this.leadsForToday(state) * demandMultiplier);
    for (let i = 0; i < count; i++) {
      if (state.leads.length >= BALANCE.leads.maxOpen) break;
      const lead = this.makeLead(state);
      if (lead) state.leads.push(lead);
    }
  }

  private makeLead(state: GameState): Lead | null {
    // Prefer customers who are not already holding an open lead, and weight
    // toward satisfied ones - good work brings the same people back.
    const busy = new Set(state.leads.map((l) => l.customerId));
    // Districts open up as the player's name gets around. Hillcrest is
    // visible from the first morning and unreachable as work until then,
    // which is what makes driving up there later feel like arriving
    // somewhere rather than walking further.
    const available = state.customers.filter(
      (c) => !busy.has(c.id) && districtOpen(c, state),
    );
    if (available.length === 0) return null;

    const customer = this.rng.weighted(
      available,
      (c) => 0.4 + c.satisfaction + loyaltyWeight(c),
    );
    const lot = CUSTOMER_LOTS.find((l) => l.id === customer.locationId);
    if (!lot) return null;
    const lotSize = lot.lotSize ?? 'medium';

    const base = BALANCE.yard.payBySize[lotSize];
    const repMult =
      1 +
      Math.min(
        BALANCE.yard.maxReputationPayBonus,
        state.player.reputation * BALANCE.yard.payPerReputation,
      );
    const variance = this.rng.float(0.9, 1.12);
    // Fussy customers and awkward properties pay more. That is the whole
    // point: without it, the harder job is strictly worse and nobody would
    // ever take it.
    const t = traitsOf(customer);
    // Pay tracks the time the property actually costs, so a difficult job is
    // worth more in total but the same per minute. The decision it poses is
    // about scheduling - do you have a block of the day big enough - rather
    // than a hidden tax on taking the interesting-looking job.
    const traitMult = difficultyMultiplier(customer)
      * (1 + (t.expectation - MEAN.expectation) * 0.3);
    const payout = Math.round(
      base * repMult * variance * traitMult * tierPayMultiplier(lot.tier),
    );

    const lead: Lead = {
      id: this.ids.next('lead'),
      customerId: customer.id,
      customerName: customer.name,
      locationId: customer.locationId,
      lotSize,
      payout,
      // An impatient customer's lead is gone in a couple of hours; a relaxed
      // one will wait most of a day.
      expiresAtMinute: state.time.totalMinutes
        + BALANCE.leads.expiryHours * 60 * (1 + (t.patience - MEAN.patience) * 0.9),
      fromContract: false,
      assignedEmployeeId: null,
      accepted: false,
    };
    this.bus.emit({
      type: 'lead.generated',
      leadId: lead.id,
      customerName: customer.name,
      payout,
    });
    return lead;
  }

  /** Contracts create their own leads on schedule. */
  generateContractLeads(state: GameState): void {
    for (const customer of state.customers) {
      const contract = customer.contract;
      if (!contract) continue;
      if (state.time.day < contract.nextDueDay) continue;
      if (state.leads.some((l) => l.customerId === customer.id)) continue;

      const lot = CUSTOMER_LOTS.find((l) => l.id === customer.locationId);
      const lead: Lead = {
        id: this.ids.next('lead'),
        customerId: customer.id,
        customerName: customer.name,
        locationId: customer.locationId,
        lotSize: lot?.lotSize ?? 'medium',
        payout: contract.payPerVisit,
        expiresAtMinute: state.time.totalMinutes + BALANCE.leads.expiryHours * 60,
        fromContract: true,
        assignedEmployeeId: null,
        accepted: true, // contracted work is already agreed
      };
      state.leads.push(lead);
      contract.nextDueDay = state.time.day + contract.intervalDays;
    }
  }

  acceptLead(state: GameState, leadId: string): boolean {
    const lead = state.leads.find((l) => l.id === leadId);
    if (!lead || lead.accepted) return false;
    lead.accepted = true;
    return true;
  }

  declineLead(state: GameState, leadId: string, reputation: ReputationSystem): boolean {
    const lead = state.leads.find((l) => l.id === leadId);
    if (!lead) return false;
    state.leads = state.leads.filter((l) => l.id !== leadId);
    reputation.add(state, BALANCE.reputation.perDeclinedLead);
    return true;
  }

  expireLeads(state: GameState, reputation: ReputationSystem): void {
    const now = state.time.totalMinutes;
    const expired = state.leads.filter(
      (l) => l.expiresAtMinute <= now && l.assignedEmployeeId === null,
    );
    if (expired.length === 0) return;

    for (const lead of expired) {
      if (lead.fromContract) {
        // Missing contracted work is much worse than letting a cold lead go.
        const customer = state.customers.find((c) => c.id === lead.customerId);
        if (customer?.contract) {
          customer.contract.missed += 1;
          customer.satisfaction = Math.max(0, customer.satisfaction - 0.25);
          reputation.add(state, BALANCE.reputation.perMissedContract);
          if (customer.contract.missed >= 2) {
            this.bus.emit({
              type: 'contract.lost',
              customerName: customer.name,
              reason: 'missed visits',
            });
            customer.contract = null;
          }
        }
      } else {
        reputation.add(state, BALANCE.reputation.perExpiredLead);
        state.statistics.leadsExpired += 1;
        this.bus.emit({
          type: 'lead.expired',
          leadId: lead.id,
          customerName: lead.customerName,
        });
      }
    }
    const expiredIds = new Set(expired.map((l) => l.id));
    state.leads = state.leads.filter((l) => !expiredIds.has(l.id));
  }

  /** Called after a lead's work is finished and paid. */
  completeLead(state: GameState, lead: Lead, quality: number): void {
    const customer = state.customers.find((c) => c.id === lead.customerId);
    state.leads = state.leads.filter((l) => l.id !== lead.id);
    if (!customer) return;

    customer.jobsCompleted += 1;
    customer.lastServicedDay = state.time.day;
    // Judged against their own standard, not a global one. The same finish
    // that earns a nod from one household earns a complaint from the one next
    // door, which is what makes expectation worth reading before accepting.
    const bar = expectationBar(customer);
    const delta = quality >= bar ? 0.14 : -0.2;
    customer.satisfaction = Math.max(0, Math.min(1, customer.satisfaction + delta));

    if (customer.contract) {
      customer.contract.missed = 0;
      return;
    }

    // A pleased customer with a registered contractor may offer regular work.
    const eligible =
      state.business.registered &&
      state.player.reputation >= BALANCE.leads.contractMinReputation &&
      customer.satisfaction >= 0.7 &&
      customer.jobsCompleted >= 2;

    if (eligible && this.rng.chance(BALANCE.leads.contractOfferChance)) {
      const payPerVisit = Math.round(lead.payout * BALANCE.leads.contractPayMultiplier);
      customer.contract = {
        id: this.ids.next('contract'),
        customerId: customer.id,
        payPerVisit,
        intervalDays: BALANCE.leads.contractIntervalDays,
        nextDueDay: state.time.day + BALANCE.leads.contractIntervalDays,
        missed: 0,
      };
      this.bus.emit({
        type: 'contract.signed',
        customerName: customer.name,
        weeklyValue: payPerVisit,
      });
    }
  }

  getLead(state: GameState, id: string): Lead | undefined {
    return state.leads.find((l) => l.id === id);
  }

  getCustomer(state: GameState, id: string): Customer | undefined {
    return state.customers.find((c) => c.id === id);
  }

  /** Distance in metres from a reference location; used for lead sorting in the UI. */
  distanceFrom(fromLocationId: string, lead: Lead): number {
    return distanceBetween(fromLocationId, lead.locationId);
  }
}

/**
 * How well a household knows the player.
 *
 * The brief asked for repeat clients, and the failure mode to avoid is
 * "repeat" meaning only that a name recurs in a list. A relationship has to be
 * legible before the job (on the card) and worth something after it (the tip),
 * or it is bookkeeping. It deliberately does NOT change the payout: value that
 * appears from nowhere is what broke the balance sweep three times when traits
 * were added, so loyalty moves who calls and how generously they tip, and
 * leaves the price of the work alone.
 */
export type Relationship = 'new' | 'returning' | 'regular' | 'loyal';

export function relationshipOf(customer: Customer): Relationship {
  const cfg = BALANCE.leads;
  if (customer.jobsCompleted >= cfg.loyalAfterJobs
    && customer.satisfaction >= cfg.loyalMinSatisfaction) return 'loyal';
  if (customer.jobsCompleted >= cfg.regularAfterJobs
    && customer.satisfaction >= cfg.regularMinSatisfaction) return 'regular';
  if (customer.jobsCompleted > 0) return 'returning';
  return 'new';
}

/** Weight added to a customer's chance of being the next caller. */
export function loyaltyWeight(customer: Customer): number {
  const rung = relationshipOf(customer);
  const scale = rung === 'loyal' ? 1 : rung === 'regular' ? 0.6 : rung === 'returning' ? 0.25 : 0;
  return BALANCE.leads.loyaltyLeadWeight * scale;
}

/** The district a customer's street belongs to; untiered lots read as starter. */
export function tierOf(locationId: string): 'starter' | 'middle' | 'wealthy' {
  return CUSTOMER_LOTS.find((l) => l.id === locationId)?.tier ?? 'starter';
}

/** Whether the player's reputation is enough to be called to this district. */
export function tierUnlocked(customer: Customer, reputation: number): boolean {
  return reputation >= BALANCE.yard.tierMinReputation[tierOf(customer.locationId)];
}

/**
 * Whether what is in the driveway can get a mower to this district.
 *
 * A bicycle counts as a vehicle for the middle districts and not for
 * Hillcrest: `travelFactor` is what tells the two apart, and the bicycle is
 * the only entry in the catalogue that is ridden rather than driven.
 * Comparing against its id directly would break the moment a second bicycle
 * is added, so this asks the question the design actually means - is this a
 * vehicle you could put a full kit in.
 */
export function vehicleAllows(
  tier: 'starter' | 'middle' | 'wealthy', vehicleId: string | null,
): boolean {
  const need = BALANCE.yard.tierMinVehicle[tier];
  if (need === 'none') return true;
  if (!vehicleId) return false;
  if (need === 'any') return true;
  const def = VEHICLES_BY_ID.get(vehicleId);
  return def !== undefined && def.carriesKit;
}

/** Whether the player can be called to this district at all, right now. */
export function districtOpen(customer: Customer, state: GameState): boolean {
  const tier = tierOf(customer.locationId);
  return state.player.reputation >= BALANCE.yard.tierMinReputation[tier]
    && vehicleAllows(tier, state.player.vehicleId);
}

/**
 * Why a district is shut, for the phone to say out loud.
 *
 * Returns null when it is open. The point of this existing at all is that a
 * player whose lead board quietly stops growing has no way to tell whether
 * they need a better name or a better vehicle, and guessing wrong costs them
 * days.
 */
export function districtBlockedBy(
  tier: 'starter' | 'middle' | 'wealthy', state: GameState,
): 'reputation' | 'vehicle' | null {
  if (state.player.reputation < BALANCE.yard.tierMinReputation[tier]) return 'reputation';
  if (!vehicleAllows(tier, state.player.vehicleId)) return 'vehicle';
  return null;
}

/** What a district pays over the starter blocks. */
export function tierPayMultiplier(tier: 'starter' | 'middle' | 'wealthy' | undefined): number {
  return BALANCE.yard.tierPayMultiplier[tier ?? 'starter'];
}

/**
 * The range each trait is drawn from, and the mean that follows.
 *
 * Every trait effect is centred on its mean, so the average customer is
 * exactly the customer the game had before traits existed. That is not
 * fussiness: the first version scaled pay up from a floor while leaving time
 * near it, which handed the player a ~30% raise and broke four checks in the
 * balance sweep at once - a run went unrecoverably into debt, and both
 * "no dominant strategy" guards failed. Traits redistribute value between
 * customers. They must not create it.
 */
export const TRAIT_RANGE = {
  expectation: [0.2, 0.95],
  patience: [0.25, 1],
  generosity: [0, 1],
  difficulty: [0.15, 1],
} as const;

const MEAN = {
  expectation: (TRAIT_RANGE.expectation[0] + TRAIT_RANGE.expectation[1]) / 2,
  patience: (TRAIT_RANGE.patience[0] + TRAIT_RANGE.patience[1]) / 2,
  generosity: (TRAIT_RANGE.generosity[0] + TRAIT_RANGE.generosity[1]) / 2,
  difficulty: (TRAIT_RANGE.difficulty[0] + TRAIT_RANGE.difficulty[1]) / 2,
};

/**
 * Traits for a customer that may predate them.
 *
 * Neutral middle values rather than a throw: a save from before traits
 * existed is a valid save, and the migration fills these in properly. This is
 * the belt to that braces.
 */
export function traitsOf(customer: Customer): CustomerTraits {
  return customer.traits ?? { ...MEAN };
}

/**
 * The finished quality this customer counts as a good job.
 *
 * Anchored on the global threshold and spread either side of it, so the
 * average customer still behaves exactly as they did before traits existed
 * and the balance harness's expectations still hold.
 */
export function expectationBar(customer: Customer): number {
  const t = traitsOf(customer);
  return BALANCE.yard.qualityGoodThreshold + (t.expectation - MEAN.expectation) * 0.10;
}

/** How much longer this customer's property takes than its size suggests. */
/**
 * How much harder this property is to do well.
 *
 * Difficulty is expressed as *quality*, not as time, and that was a deliberate
 * change after measuring. Making a hard property take longer looked obvious
 * and wrecked the economy: a day is a fixed budget of minutes and jobs are
 * taken one at a time, so variance in job length reduces throughput even when
 * its mean is exactly 1 - a job that runs 17% long costs 17% of the day, while
 * one that runs 17% short rarely buys a whole extra job. At +/-20% the balance
 * sweep's median goal day went from 16 to 53; at +/-7% it was still 40.
 *
 * Quality has no such convexity, and it makes a better mechanic anyway: an
 * overgrown property is one you need better equipment to do justice to, which
 * ties the client you choose to the kit you have bought.
 */
export function difficultyPenalty(customer: Customer): number {
  return (traitsOf(customer).difficulty - MEAN.difficulty) * 0.13;
}

/** Pay compensates for difficulty, so the hard job is worth taking. */
export function difficultyMultiplier(customer: Customer): number {
  return 1 + (traitsOf(customer).difficulty - MEAN.difficulty) * 0.34;
}
